import React, { useMemo } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAccessContext } from '../../context/AccessContext'
import { CommandProvider, useCommands, useWorkspaceCommands } from '../../context/CommandContext'
import type { CommandContext } from '../../services/commandRegistry'
import type { WorkspaceView } from '../workspaces/admin/navigationRegistry'
import { useAdminExperience } from '../../context/AdminExperienceContext'
import { useIndustryTerminology } from '../../hooks/useIndustryTerminology'
import { useI18n } from '../../i18n/I18nContext'
import { adminNavigationCommands, workspaceNavigationCommands } from '../../services/navigationCommands'

function NavigationCommands() {
  const { context } = useCommands()
  const { t } = useI18n()
  const industry = useIndustryTerminology()
  const commands = useMemo(() => context ? [
    ...workspaceNavigationCommands(context, { t, ...industry }),
    ...adminNavigationCommands(context, { t, ...industry }),
  ] : [], [context, t, industry])
  useWorkspaceCommands(commands)
  return null
}

export function WorkspaceCommands({ workspace, route, children }: React.PropsWithChildren<{ workspace: WorkspaceView; route: string }>) {
  const { user, loading: authLoading } = useAuth()
  const { accessContext, selectedTenantId, currentOrganization, loading, error, authenticationRequired, hasPermission, hasAnyPermission } = useAccessContext()
  const { isSimple } = useAdminExperience()
  const ready = !authLoading && !loading && !error && !authenticationRequired && Boolean(user?.id
    && accessContext?.currentUser?.id === user.id && (!selectedTenantId || selectedTenantId === accessContext?.tenantId))
  // Include the complete verified access snapshot: grants, relationships, MFA/capabilities
  // and entitlements all retire registrations. Tenantless personal/platform access is valid.
  const scopeKey = ready ? JSON.stringify([user!.id, selectedTenantId, currentOrganization?.id, workspace, route, accessContext, isSimple]) : null
  const context = useMemo<CommandContext | null>(() => ready ? {
    workspace, hasPermission,
    hasFeature: feature => accessContext?.billingEntitlements?.features?.[feature]?.enabled === true,
    navigation: { input: { accessContext, selectedTenantId, currentOrganization, hasAnyPermission }, isSimple, route },
  } : null, [ready, workspace, hasPermission, accessContext, selectedTenantId, currentOrganization, hasAnyPermission, isSimple, route])
  return <CommandProvider scopeKey={scopeKey} context={context} historyScope={ready ? {
    userId: user!.id, tenantId: accessContext?.tenantId || null, workspaceId: workspace, organizationId: currentOrganization?.id,
  } : null}><NavigationCommands />{children}</CommandProvider>
}
